<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
            <div>

                <div
                    v-if="$currentUserInfo.is_authenticated"
                    class="mt-3"
                >
                    <b-link 
                        href="/" 
                        class="font-weight-600"
                    >&larr; Back to Dashboard</b-link>
                </div>

                <transition name="fade-fast" mode="out-in">
                    <div
                        v-if="showingPermissionDenied"
                        key="showingPermissionDenied"
                        class="mt-3 min-height-300 d-flex align-items-center justify-content-center"
                    >
                        <div>
                            <h5>You no longer have access to this Sourcing Event</h5>
                            <p class="text-muted">Please contact Supplios Support if you think this is an error.</p>
                        </div>
                    </div>

                    <div 
                        v-else-if="!hasRfx"
                        key="rfxLoadingHeader"
                        class="mt-3 w-100 height-150 min-height-150"
                    >
                        <!-- <div class="h-100">                
                            </div> -->
                        <busy-spinner text="Loading..."></busy-spinner>
                    </div>

                    <div v-else key="rfxLoaded" class="mt-3 overflow-hidden">
                        <div 
                            class="row mb-4"
                        >
                            <div class="col">
                                <div class="border rounded bg-white shadow-sm overflow-hidden">            
                                    <div class="px-4 py-3 d-flex justify-content-between align-items-center">  <!-- top of header -->
                                        <div class="">
                                            <div class="mb-2 d-flex">
                                                <div class="text-muted text-uppercase font-weight-bold mr-3">{{ rfx.rfx_type_display }}</div>
                                                <status-object-badge
                                                    :status-object="rfx.state_dict"
                                                    text-uppercase
                                                    font-size="0.9rem"
                                                ></status-object-badge>
                                                <!-- <span 
                                                    :class="`ml-3 chip chip-${ rfx.state_dict.color_variant } font-weight-bold text-uppercase`"
                                                >{{ rfx.state_dict.title }}
                                                </span> -->
                                            </div>
                                            <h1 class="">{{ rfx.title }}</h1>
                                        </div>
                                        <div class="">
                                            <img
                                                :src="tenantInfo.settings.logo_url" 
                                                width="150"
                                                :alt="`${tenantInfo.tenant_name} Logo`"
                                            >
                                        </div>
                                    </div>  <!-- buttons of header -->
                                    <b-nav 
                                        id="rfx-detail-nav"
                                        justified 
                                        class="bg-gray-50 d-flex border-top"
                                    >
                                        <b-nav-item 
                                            :active="activeTabName === 'rfxTab'" 
                                            @click="activateTab('rfxTab')" 
                                            class="border-right"
                                        >
                                            <b-icon icon="card-checklist" variant="primary" class="font-size-150 mr-2"></b-icon>
                                            View {{ rfx.rfx_type }}
                                        </b-nav-item>

                                        <b-nav-item 
                                            :active="activeTabName === 'responseTab'" 
                                            @click="activateTab('responseTab')" 
                                            class="border-right"
                                            :disabled="$currentUserInfo.is_internal"
                                        >
                                            <b-icon icon="reply" variant="primary" class="font-size-150 mr-2"></b-icon>
                                            {{ responseIsStarted ? 'Your Response' : 'Details & Next Steps' }}
                                            <div 
                                                v-if="showNeedsAttention" 
                                                v-b-tooltip.hover
                                                :title="respData.needs_attention_external_text"
                                                class="text-danger font-size-70 font-weight-bold text-center my-1 px-2"
                                            >
                                                <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                                            </div>
                                        </b-nav-item>
                                        
                                        <b-nav-item 
                                            :active="activeTabName === 'messageTab'" 
                                            @click="activateTab('messageTab')" 
                                            class=""
                                            :link-classes="disableMessageTab ? 'd-none' : ''"
                                            :disabled="disableMessageTab"
                                        >
                                            <b-icon icon="envelope" variant="primary" class="font-size-150 mr-2"></b-icon>
                                            <span class="mr-2">Messages</span>
                                            <new-badge 
                                                v-if="hasUnreadMessages"
                                                small 
                                                x-margin="1"
                                            ></new-badge>
                                        </b-nav-item>
                                    
                                    </b-nav>
                                </div>
                            </div>
                        </div>
                    

                        <div v-if="hasRfx && !loading" class="row mb-5">

                            <div class="col-12 col-md-9">  <!-- CONTENT COLUMN -->
                                <b-tabs lazy no-nav-style nav-class="d-none">
                                    <!-- ALWAYS SHOW THE TABS, BUT DON'T ALWAYS HAVE TO SHOW THE CONTENT -->
                                    <b-tab 
                                        :active="activeTabName === 'rfxTab'" 
                                        ref="rfxTab"
                                    >
                                        <!-- we WILL fetch the RFX in here also, but with lots more fields (different field group) -->
                                        <portal-rfx-detail
                                            :rfx-id="rfxId"
                                            :quicklink-slug="quicklinkSlug"
                                            class="spl-animate spl-animate-fade-up"
                                            @permission-denied="handlePermissionDenied"
                                        ></portal-rfx-detail>
                                    </b-tab>

                                    <b-tab 
                                        :active="activeTabName === 'responseTab'" 
                                        ref="responseTab"
                                    >
                                        <!-- don't add externalMessageThreadId to this and it just won't show it -->
                                        <portal-rfx-response-combined
                                            v-if="response_id && responseIsStarted"
                                            key="hasResponseStarted"
                                            :responseId="response_id"
                                            class="spl-animate spl-animate-fade-up"
                                            @submitted="handleResponseSubmitted"
                                        ></portal-rfx-response-combined>
                                        <div 
                                            v-else-if="response_id && responseIsDeclined"
                                            key="responseDeclined"
                                            class="w-100 height-300 min-height-300 d-flex align-items-center justify-content-center"
                                        >
                                            <div class="text-center">
                                                <h5>You have declined to participate in this {{ rfx.rfx_type }}</h5>
                                                <p>If you change your mind, you can come back here and click the button to undo your choice.</p>
                                                <p>This is possible until the buyer closes the {{ rfx.rfx_type }} or otherwise removes your participation.</p>
                                                <b-button href="/" variant="subtle-primary" class="mt-4">&larr; Back to Dashboard</b-button>
                                            </div>
                                        </div>
                                        <div 
                                            v-else-if="response_id && !responseIsStarted"
                                            key="responseNotStartedYet"
                                            class="w-100 height-300 min-height-300 d-flex align-items-center justify-content-center"
                                        >
                                            <div class="text-center">
                                                <h5>Click on the green 'View Details & Next Steps' button to begin</h5>
                                            </div>
                                        </div>
                                        <!-- <div
                                            v-else-if="isQuicklinkView && quicklinkCanRegister"
                                            key="quicklinkCanRegisterOk"
                                            class="w-100 height-300 min-height-300 d-flex align-items-center justify-content-center"
                                        >
                                            <div class="text-center">
                                                <h5>Click on the 'Respond Now' button to begin</h5>
                                            </div>
                                        </div> -->
                                        <div v-else key="noResponseFound" class="w-100 height-300 min-height-300 d-flex align-items-center justify-content-center">
                                            <div class="">
                                                <h5>{{ noResponseFoundMessage }}</h5>
                                            </div>
                                        </div>
                                    </b-tab>

                                    <b-tab 
                                        :active="activeTabName === 'messageTab'" 
                                        ref="messageTab"
                                    >
                                        <div class="bg-white p-4 border rounded overflow-hidden spl-animate spl-animate-fade-up">
                                            <base-message-thread
                                                v-if="externalMessageThreadId"
                                                :thread-id="externalMessageThreadId"
                                                title="Messages with Buyer"
                                                subtitle="Ask questions, or respond to buyer feedback.  You will receive any replies by email, and here in the app."
                                                message-name="message"
                                                create-verb="New"
                                                editor-style="rich"
                                                thread-type="email"
                                                hide-internal-members
                                                @unread-status="handleMessageThreadUnreadStatus"
                                            ></base-message-thread>
                                        </div>
                                    </b-tab>

                                </b-tabs>
                            </div>

                            <div class="col-12 col-md-3">  <!-- narrow column -->
                                
                                <div 
                                    class="spl-animate spl-animate-fade-left" 
                                    style="position: sticky; top: 20px;"
                                >
                                    <b-list-group class="rounded shadow-sm font-size-90">
                                        <b-list-group-item 
                                            v-if="rfx.close_date_planned"
                                            class="py-4"
                                        >
                                            <p class="special-text text-muted mb-3">Response Deadline</p>
                                            <div class="font-weight-bold mb-2 d-flex align-items-baseline">
                                                <b-icon icon="calendar2-event-fill" variant="secondary" class="mr-2"></b-icon>
                                                <local-date-from-iso :date="rfx.close_date_planned" preset="DATE_FULL"></local-date-from-iso>
                                            </div>
                                            <div class="mb-2 d-flex align-items-baseline">
                                                <b-icon icon="clock" variant="secondary" class="mr-2"></b-icon>
                                                <div>
                                                    <local-date-from-iso :date="rfx.close_date_planned" format-string="t" class="font-weight-bold mr-2"></local-date-from-iso>
                                                    <local-date-from-iso :date="rfx.close_date_planned" format-string="ZZZZZ" class="small text-muted"></local-date-from-iso>
                                                </div>
                                                
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="py-4">
                                            <p class="special-text text-muted mb-3">Company Contact</p>
                                            <p class="mb-1 font-weight-bold mb-1">
                                                <b-icon icon="person" variant="secondary" class="mr-2"></b-icon>
                                                {{ rfx.sourcing_owner_name }}
                                            </p>
                                            <p v-if="rfx.show_real_owner_email">
                                                <b-icon icon="envelope" variant="secondary" class="mr-2"></b-icon>
                                                <span class="small">{{ rfx.sourcing_owner_email }}</span>
                                            </p>
                                        </b-list-group-item>
                                        <b-list-group-item v-if="rfx.is_confidential" class="py-3">
                                            <p class="text-danger-lighter letter-spacing-wide font-weight-900 text-uppercase mb-0 font-size-110 text-center">Confidential</p>
                                        </b-list-group-item>
                                    </b-list-group>

                                    <!-- Show Current Response Status -->
                                    <div 
                                        v-if="!!respData && !!respData.state_dict"
                                        class="mt-4"
                                    >
                                        <div class="font-weight-bold">Your Response Status</div>
                                        <b-list-group
                                            class="rounded shadow-sm font-size-90"
                                        >
                                            <b-list-group-item>
                                                <status-object-badge
                                                    :status-object="respData.state_dict"
                                                    text-uppercase
                                                    font-size="0.9rem"
                                                ></status-object-badge>
                                            </b-list-group-item>
                                            <b-list-group-item
                                                v-for="notice in respData.rfx_award_notices"
                                                :key="notice.id"
                                                class="bg-success-50"
                                            >
                                                {{ notice.note }}
                                                <div class="mt-2">
                                                    <b-button 
                                                        size="sm"
                                                        variant="success"
                                                        :href="notice.external_url"
                                                    >
                                                        More Details &rarr;
                                                    </b-button>
                                                </div>
                                            </b-list-group-item>
                                            <b-list-group-item
                                                v-if="showNeedsAttention"
                                            >
                                                <div class="font-size-80 mb-1">
                                                    <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                                                    <span class="text-danger font-weight-bold text-uppercase">NEEDS ATTENTION</span>
                                                </div>
                                                <div class="small">
                                                    {{ respData.needs_attention_external_text }}
                                                </div>
                                            </b-list-group-item>
                                        </b-list-group>
                                    </div>

                                    <!-- NOTE - if you don't destroy this component when changing state, have to make sure it re-fetches the available actions -->
                                    <!-- You can call certain methods on this component too... to createResponse/callTransitionTrigger, etc. -->
                                    <!-- can call .refresh() on this component to refresh the actions, listen for new emitted event for what is available -->
                                    <portal-rfx-response-actions
                                        ref="actionsComponent"
                                        :key="lastFetchTimeString"
                                        :rfx-id="rfxId"
                                        :quicklink-slug="quicklinkSlug"
                                        class="mt-4 portal-rfx-response-actions-outer"
                                        @updated="handleResponseActionUpdated"
                                        @update-can-create="handleUpdateCanCreate"
                                        @update-available-triggers="handleUpdateAvailableTriggers"
                                        @update-quicklink-can-register="handleQuicklinkCanRegister"
                                    ></portal-rfx-response-actions>
                                    <!-- NOTE - Oct 5 2022 - the update-... event payloads might not be refreshing in child component... currently relying on @updated callback refreshing rfx, which destroys this -->

                                    <portal-rfx-response-submission-list
                                        v-if="response_id"
                                        ref="submissionList"
                                        class="mt-4"
                                        :response-id="response_id"
                                    ></portal-rfx-response-submission-list>

                                </div>
                            </div>  <!-- end of narrow column -->

                        </div>
                    </div>

                </transition>

            </div>
    
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, tenantInfoMixin, currentUserInfoMixin } from '../../js/custom-vue-mixins';
// tenantInfoMixin adds a 'tenantInfo' to computed properties
import { Rfx } from '../../js/models/rfx';
import { RfxResponse } from '../../js/models/rfxResponse';
import { FormatDateTimeLocal } from '../../js/filters-helpers';
import StatusObjectBadge from '../functional-comps/StatusObjectBadge';
import PortalRfxResponseSubmissionList from './PortalRfxResponseSubmissionList.vue';


// TODO - put some special event emitters / handlers on this component and child components
// so if there is a permission denied response (because they got rejected or can no longer see the RFQ)
// then it does something smart... like reload the entire page or show some component state where they have no access

export default {
    name: 'PortalRfxWrapper',
    mixins: [
        errorHandling,
        tenantInfoMixin,
        currentUserInfoMixin,
    ],
    components: {
        StatusObjectBadge,
        PortalRfxResponseSubmissionList,
    },
    props: {
        rfxId: {
            type: String,
            required: true,
        },
        initialTab: {  // use same tab names as in URL querystring (rfx, response, messages)
            // OPTIONAL - can set this if you want to start on a certain page if embedding somewhere
            type: String,
            required: false,
        },
        quicklinkSlug: {
            // must provide this (and pass in API call config.params.quicklink_slug) if its a quicklink view
            // ONLY populate it if it IS a quicklink view, or else endpoints will not check TU and it will deny them
            type: String,
            default: '',
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            rfx: {},  // stash here after api call
            respData: {},  // if you have one, will be here
            response_id: '',  // stash here after api call
            lastFetchTimeString: '',  // stash the epoch ms time as a string here when doing getRfx() -- used as a key on things we want to update when doing getRfx() - String(new Date().getTime())
            activeTabName: 'rfxTab',
            localInitialTab: '',  // will put one here if we get it from URL or from prop
            localHasUnreadMessages: null, // start with null, then will update this from @unread-status handler on base-message-thread component -- its the best source of truth
            showingPermissionDenied: false,  // if we get a 403 (Forbidden / PermissionDenied) we'll set this to TRUE to show a special error thing
            //externalMessageThreadId: '',  // stash after getting from API - resp.external_message_thread_id
            tabNameDecoderRing: {
                // FIXED VALUES - do not mutate - format is urlTabName: localTabName
                rfx: 'rfxTab',
                response: 'responseTab',
                message: 'messageTab',  // try to catch both
                messages: 'messageTab',
            },
            // DATA THAT IS SENT FROM CHILD PortalRfxResponseActions - set these in handlers here
            canCreate: null,  // set in event handler for PortalRfxResponseActions
            quicklinkCanRegister: null,  // set in event handler for PortalRfxResponseActions
            availableTriggers: [],  // set in event handler for PortalRfxResponseActions
        }
    },
    computed: {
        hasRfx() {
            return !!this.rfx.id;
        },
        hasResponse() {
            return !!this.response_id;
        },
        externalMessageThreadId() {
            return this.respData?.external_message_thread_id ?? '';
        },
        hasUnreadMessages() {
            // this comes from 2 places -- this.localHasUnreadMessages -- which is updated from @unread-status on base-message-thread (best source of truth)
            // also from respData -- can get from there first if localHasUnreadMessages is null/undefined
            return this.localHasUnreadMessages ?? (this.respData?.external_thread_tu_read_status?.has_unread ?? false);
        },
        responseIsStarted() {
            // return true if the response is started (or later)
            return this.respData?.is_submitted || this.respData?.sm_state === 'started';
        },
        responseIsDeclined() {
            return this.respData?.sm_state === 'declined';
        },
        noResponseFoundMessage() {
            // there are multiple situations where there will be no response -- we'll show different messages for each
            if (this.canCreate) return `Click on the 'Respond Now' button to begin`;
            if (this.isQuicklinkView && this.quicklinkCanRegister) return `Click on the 'Respond Now' button to begin`;
            return `No Response Found`;
        },
        showNeedsAttention() {
            // only actually show 'NEEDS ATTENTION' if it has been submitted
            return this.respData?.state_dict?.is_submitted && this.respData?.needs_attention_external;
        },
        isQuicklinkView() {
            return !!this.quicklinkSlug;
        },
        disableMessageTab() {
            // we disable it for internal users, or if not logged in (quicklink view)
            // OR if there is no response... there will be no message thread yet
            // can simplify all with just one check...
            return !this.hasResponse;
            //return this.$currentUserInfo.is_internal || !this.$currentUserInfo.is_authenticated;
        },
    },
    async mounted() {
        // what wins between prop and URL string?  originally you had prop... 
        // but you are using the same page/template for both messages and response URLs, so prop will be the same, only URL string will be different
        // so to support that, you need URL string to win
        const tabNameFromUrl = this.getTabNameFromUrl();  // might not have one
        if (tabNameFromUrl) {
            this.setLocalInitialTab(tabNameFromUrl);
        } else if (this.initialTab) {  // if we had something set in props
            this.setLocalInitialTab(this.initialTab);
        } 
        if (this.localInitialTab) {
            // if we got one using above things, activate it -- only happens here in mounted()
            this.activateTab(this.localInitialTab);
        }
        await this.getRfx();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getRfx({skipLoading = false} = {}) {  // pass {skipLoading: true} if you want no loading
            if (!skipLoading) {
                this.loading = true;
            }
            // NOTE - this serves EXTERNAL and INTERNAL and QUICKLINK views 
            // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value if there is NO TU, and its a quicklink view
            let requestConfig = null;
            if (this.isQuicklinkView) {
                requestConfig = {params: {quicklink_slug: this.quicklinkSlug}};
            }
            try {
                const { data } = await suppliosApi.rfx.get_detail_external_with_response(this.rfxId, requestConfig);
                // data is an object with { rfx, response_id, response }  -- response_id may be null if no response found for this TU
                // does not have ALL the data from Rfx... just enough for this wrapper component
                this.rfx = Object.assign({}, new Rfx(data.rfx));
                this.respData = data.response ? Object.assign({}, new RfxResponse(data.response)) : {};
                this.response_id = data.response_id;
                this.lastFetchTimeString = String(new Date().getTime());  // used as a key on things we want to update (destroy/recreate) when refetching this
            } catch (err) {
                // if we get a 403 Forbidden (PermissionDenied in Django) then show something special - it's likely that the TU got rejected or no longer as access to the RFX
                // if some other error, then its just an error
                if (err.response?.status === 403) {  // if we got 403, 
                    this.handlePermissionDenied();
                } else {
                    this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
                }
            }
            // don't need to do anything with active tab here... that will get set in mounted, OR in the hook for dealing with state changes (before this is called)
            this.loading = false;
        },
        getTabNameFromUrl: function() {
            // our poor mans router
            const queryString = window.location.search;
            const urlHash = window.location.hash;  // this will have leading # on it -- but it DOES get returned from server, just like qs, so you could use either
            const urlParams = new URLSearchParams(queryString);
            const urlTabName = urlParams.get('tab');
            console.log(`tab name from querystring: ${urlTabName}`);
            console.log(`location hash is: ${urlHash}`);
            // RETURN it - don't set anything here
            return urlTabName ?? '';
        },
        setLocalInitialTab(tabName) {
            // use this to set the localInitialTab from url value or prop value
            this.localInitialTab = this.tabNameDecoderRing[tabName];
        },
        activateTab(tabName) {
            const blockedForInternalTabs = ['responseTab', 'messageTab'];
            if (this.$currentUserInfo.is_internal && blockedForInternalTabs.includes(tabName)) {
                console.log(`Internal user tried to access tab ${tabName} but its blocked`);
                // we WILL set the default here for internal users, in case it was not set otherwise
                this.activeTabName = 'rfxTab';  // remove this if you'll allow setting different tabs for internal users, if you hide the things in those tabs
                return;
            }
            // need to add blocking logic at the top... before calling .activate() or this.activeTabName
            this.activeTabName = tabName;  // put here in case tabs aren't loaded yet
            const tabComponent = this.$refs[tabName];
            if (!tabComponent) return;
            tabComponent.activate();
        },
        handleResponseActionUpdated(stateChangeResponsePayload) {
            // called from @updated on PortalRfxResponseActions
            // payload will be entire thing from StateMachineTransitions api response
            // will have at least { success, message, object, redirect_url }
            // for RfxResponse, we should at least have these things in object: { id, pk, sm_state, state_dict } (and maybe vendor_url... but that might be a dead-end if state just changed to something where they can't see that page)
            // have to reload everything
            // AND... set this.activeTabName to something that makes sense for the action that happened?
            // if you just changed state (ie: declined) and no longer have an active response, doesn't make sense to be on the Response tab
            // or if you just created a response (portal create) you should jump to Response tab instead of staying on View Details tab
            // Putting the logic here instead of in this.getRfx() -- since these things only happen when changing state... otherwise we should just honor the tab set in prop or URL query param
            const newState = stateChangeResponsePayload.object?.sm_state;
            const statesForResponseTab = ['invited', 'started', 'declined', 'submitted', 'awarded', 'rejected']  // the states where it makes sense to change to the responses tab - not sure about awarded, rejected...
            // we added 'declined' to this list, and also added a message saying its declined in the response tab... so OK to show it for declined now
            // for all others, go to the Rfx details tab
            // call the reload first (trigger it, don't await it, and/or start loading=true here) then change the tab
            this.getRfx();
            if (newState && statesForResponseTab.includes(newState)) {
                this.activateTab('responseTab');
            } else {
                this.activateTab('rfxTab');
            }
            //last resort:  window.location.reload();
        },
        refreshActions() {
            // calls .refresh() on the actions component
            // call this after something else in here does something that may/could change state of response (like submitted new response)
            const actionsComp = this.$refs['actionsComponent'];
            if (!actionsComp) return;  // just return if its not in the DOM for some reason
            actionsComp.refresh();  // boom... then your listeners/handlers will update what's available
        },
        refreshSubmissionList() {
            const submissionListComp = this.$refs['submissionList'];
            if (submissionListComp) {
                submissionListComp.refresh();
            }
        },
        handleUpdateAvailableTriggers(availableTriggers) {
            // availableTriggers will be array from PortalRfxResponseActions of possible trigger names we can call
            // in case we want to show additional buttons for them here, separate from the ones that component shows itself
            this.availableTriggers = [...availableTriggers];
        },
        handleUpdateCanCreate(canCreate) {
            // canCreate will be Boolean from PortalRfxResponseActions of whether a new response can be created for this RFX by the TU
            // in case we want to show additional buttons for doing it here, separate from the one that component shows itself
            this.canCreate = canCreate;
        },
        handleQuicklinkCanRegister(quicklinkCanRegister) {
            // quicklinkCanRegister will be Boolean from PortalRfxResponseActions of whether the user can register for an account to respond to this RFX via quicklink
            // might not always be true for quicklink views... if TU is logged in and already has a response, for example
            this.quicklinkCanRegister = quicklinkCanRegister;
        },
        handleResponseSubmitted() {
            // called from @submitted event in portal-rfx-response-combined
            // we need to reload everything -- and make sure actions update too!  might have to call a method on them?
            this.getRfx({skipLoading: true});  // no loading... so things should not get destroyed... that's good, but also risky
            this.refreshSubmissionList();
            //this.refreshActions();  // need to call this separately... actions component will not get destroyed if we skip loading, so have to refresh it manually
        },
        handleMessageThreadUnreadStatus(hasUnread) {
            // payload will be Boolean, true if there are unread messages for THIS CURRENT TU
            this.localHasUnreadMessages = hasUnread;
        },
        handlePermissionDenied() {
            // this handles 403 Forbidden / PermissionDenied errors from this component, or some child components
            // if we get a 403 Forbidden (PermissionDenied in Django) then show something special - it's likely that the TU got rejected or no longer as access to the RFX
            this.showingPermissionDenied = true;
        },
        /// FILTERS
        formatDateTime(value) {
            return FormatDateTimeLocal(value);
        },
    }
}
</script>

<style lang="scss">

#rfx-detail-nav {

    li.nav-item {

        a.nav-link {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            //color: $gray-500;
            color: $gray-900; // just change 'color' with opacity instead
            text-transform: uppercase;
            font-weight: 600;
            padding-top: 1rem;
            padding-bottom: 1rem;
            border-bottom: 4px solid transparent;
            opacity: 0.6;
            transition: 0.2s all;

            &.active {
                border-bottom-color: var(--primary);
                //color: $gray-900;
                opacity: 1;
            }
            &:hover:not(.active) {
                border-bottom-color: var(--gray-200);
                //color: $gray-700;
                opacity: 1;
            }

        }
    }

}

</style>